import React, {
  useEffect,
  useState,
} from "react";

import {
  Link,
  useNavigate,
} from "react-router-dom";

import {
  getLinkedMembersApi,
  getClubsApi,
} from "../api/ClientActions";

// ==========================
// API BASE FOR IMAGE URLS
// ==========================
const API_URL = (
  process.env.REACT_APP_API_URL ||
  "https://api.bsangam.com"
)
  .replace(/\/uploads\/?$/, "")
  .replace(/\/$/, "");

const Rightchat: React.FC = () => {
  const navigate =
    useNavigate();

  const [
    members,
    setMembers,
  ] = useState<any[]>([]);

  const [
    clubs,
    setClubs,
  ] = useState<any[]>([]);

  const [
    membersLoading,
    setMembersLoading,
  ] = useState(false);

  const [
    clubLoading,
    setClubLoading,
  ] = useState(false);

  const user = JSON.parse(
    localStorage.getItem("user") ||
      "{}"
  );

  const userId =
    user?.user_id ||
    null;

  // ==========================
  // IMAGE URL HELPER
  // ==========================
  const getImageUrl = (
    path?: string | null,
    fallback =
      "/assets/images/user.png"
  ) => {
    if (
      !path ||
      String(path).trim() === ""
    ) {
      return fallback;
    }

    const imagePath =
      String(path).trim();

    if (
      imagePath.startsWith(
        "http://"
      ) ||
      imagePath.startsWith(
        "https://"
      )
    ) {
      return encodeURI(
        imagePath
      );
    }

    if (
      imagePath.startsWith(
        "/uploads/"
      )
    ) {
      return encodeURI(
        `${API_URL}${imagePath}`
      );
    }

    if (
      imagePath.startsWith(
        "uploads/"
      )
    ) {
      return encodeURI(
        `${API_URL}/${imagePath}`
      );
    }

    if (
      imagePath.startsWith(
        "profile-photos/"
      ) ||
      imagePath.startsWith(
        "logos/"
      ) ||
      imagePath.startsWith(
        "banners/"
      ) ||
      imagePath.startsWith(
        "documents/"
      ) ||
      imagePath.startsWith(
        "designs/"
      )
    ) {
      return encodeURI(
        `${API_URL}/uploads/${imagePath}`
      );
    }

    if (
      imagePath.startsWith("/")
    ) {
      return encodeURI(
        `${API_URL}${imagePath}`
      );
    }

    return encodeURI(
      `${API_URL}/uploads/${imagePath}`
    );
  };

  // ==========================
  // MEMBER PROFILE PHOTO
  // ==========================
  const getProfileImage = (
    member: any
  ) =>
    getImageUrl(
      member?.profile_photo ||
        member?.contact_photo ||
        member?.photo ||
        member?.profile_image,
      "/assets/images/user.png"
    );

  // ==========================
  // CLUB LOGO
  // ==========================
  const getClubLogo = (
    club: any
  ) =>
    getImageUrl(
      club?.logo_image ||
        club?.client_logo,
      "/assets/images/user.png"
    );

  // ==========================
  // FETCH LINKED MEMBERS
  // ==========================
  const fetchLinkedMembers =
    async () => {
      if (!userId) {
        setMembers([]);
        return;
      }

      try {
        setMembersLoading(
          true
        );

        const result =
          await getLinkedMembersApi({
            user_id: userId,
          });

        if (
          result?.success ===
            false ||
          result?.status ===
            false
        ) {
          setMembers([]);
          return;
        }

        if (
          Array.isArray(
            result?.data
          )
        ) {
          const allMembers =
            result.data.flatMap(
              (club: any) =>
                (
                  club.members ||
                  []
                ).map(
                  (
                    member: any
                  ) => ({
                    ...member,

                    client_id:
                      club.client_id,

                    client_name:
                      club.client_name,

                    unique_slug:
                      club.unique_slug,

                    verified_status:
                      club.verified_status,

                    logo_image:
                      club.logo_image,
                  })
                )
            );

          setMembers(
            allMembers
          );
        } else {
          setMembers([]);
        }
      } catch (error) {
        console.error(
          "Joined Members Error:",
          error
        );

        setMembers([]);
      } finally {
        setMembersLoading(
          false
        );
      }
    };

  // ==========================
  // FETCH CLUBS
  // ==========================
  const fetchClubs =
    async () => {
      try {
        setClubLoading(true);

        const result =
          await getClubsApi();

        if (
          result?.success ===
            false ||
          result?.status ===
            false
        ) {
          setClubs([]);
          return;
        }

        setClubs(
          Array.isArray(
            result?.data
          )
            ? result.data
            : []
        );
      } catch (error) {
        console.error(
          "Clubs Error:",
          error
        );

        setClubs([]);
      } finally {
        setClubLoading(false);
      }
    };

  // ==========================
  // INITIAL LOAD
  // ==========================
  useEffect(() => {
    if (!userId) {
      setMembers([]);
      setClubs([]);
      return;
    }

    fetchLinkedMembers();
    fetchClubs();

    // eslint-disable-next-line
  }, [userId]);

  // ==========================
  // OPEN USERS PAGE
  // ==========================
  const handleMemberPhotoClick =
    () => {
      navigate("/users");
    };

  // ==========================
  // CLUB PAGE URL
  // ==========================
  const getClubUrl = (
    club: any
  ) => {
    if (
      club?.unique_slug
    ) {
      return `/club/${club.unique_slug}`;
    }

    return `/club/${club?.client_id}`;
  };

  // ==========================
  // SECTION HEADER
  // ==========================
  const SectionHeader = ({
    title,
    icon,
    link,
    badge,
  }: {
    title: string;
    icon: string;
    link?: string;
    badge?: number | string;
  }) => (
    <div className="bs-side-section-header">
      <div className="bs-side-section-title">
        <div className="bs-side-section-icon">
          <i className={icon} />
        </div>

        <h4>
          {title}
        </h4>
      </div>

      {badge !==
      undefined ? (
        <span className="bs-side-badge">
          {badge}
        </span>
      ) : link ? (
        <Link
          to={link}
          className="bs-side-view"
        >
          View
          <i className="feather-arrow-right" />
        </Link>
      ) : null}
    </div>
  );

  // ==========================
  // EMPTY STATE
  // ==========================
  const EmptyState = ({
    icon,
    title,
    text,
    link,
    action,
  }: {
    icon: string;
    title: string;
    text: string;
    link?: string;
    action?: string;
  }) => (
    <div className="bs-side-empty">
      <div className="bs-side-empty-icon">
        <i className={icon} />
      </div>

      <h5>
        {title}
      </h5>

      <p>
        {text}
      </p>

      {link && action && (
        <Link
          to={link}
          className="bs-side-empty-action"
        >
          {action}
        </Link>
      )}
    </div>
  );

  return (
    <div
      id="main-content-wrap"
      className="right-chat nav-wrap mt-2 right-scroll-bar active-sidebar bs-rightchat"
    >
      <div className="middle-sidebar-right-content bs-rightchat-content">
        {/* ==========================
            STORIES
        ========================== */}
        <div className="bs-side-card">
          <SectionHeader
            title="Stories"
            icon="feather-file-text"
            link="/defaultstorie"
          />

          <Link
            to="/defaultstorie"
            className="bs-side-feature"
          >
            <div className="bs-side-feature-icon bs-side-feature-icon--blue">
              <i className="feather-file-text" />
            </div>

            <div className="bs-side-feature-content">
              <h5>
                Most Viewed News
              </h5>

              <span>
                Trending News
              </span>
            </div>

            <i className="feather-chevron-right bs-side-feature-arrow" />
          </Link>
        </div>

        {/* ==========================
            VIDEOS
        ========================== */}
        <div className="bs-side-card">
          <SectionHeader
            title="Videos"
            icon="feather-play"
            link="/defaultvideo"
          />

          <Link
            to="/defaultvideo"
            className="bs-side-feature"
          >
            <div className="bs-side-feature-icon bs-side-feature-icon--red">
              <i className="feather-play" />
            </div>

            <div className="bs-side-feature-content">
              <h5>
                Latest Videos
              </h5>

              <span>
                Watch trending videos
              </span>
            </div>

            <i className="feather-chevron-right bs-side-feature-arrow" />
          </Link>
        </div>

        {/* ==========================
            MEMBERS
        ========================== */}
        <div className="bs-side-card">
          <SectionHeader
            title="Members"
            icon="feather-users"
            badge={
              members.length
            }
          />

          {membersLoading ? (
            <div className="bs-side-loading-list">
              {Array.from({
                length: 3,
              }).map(
                (_, index) => (
                  <div
                    key={index}
                    className="bs-side-skeleton-row"
                  >
                    <div className="bs-side-skeleton-avatar" />

                    <div className="bs-side-skeleton-lines">
                      <div className="bs-side-skeleton-line bs-side-skeleton-line--wide" />
                      <div className="bs-side-skeleton-line bs-side-skeleton-line--small" />
                    </div>
                  </div>
                )
              )}
            </div>
          ) : members.length >
            0 ? (
            <div className="bs-side-list">
              {members
                .slice(0, 5)
                .map(
                  (
                    member: any,
                    index: number
                  ) => {
                    const memberName =
                      member?.contact_name ||
                      member?.user_fullname ||
                      member?.member_name ||
                      "Member";

                    const memberImage =
                      getProfileImage(
                        member
                      );

                    const memberRole =
                      member?.profession ||
                      member?.designation_name ||
                      member?.contact_profession ||
                      "Member";

                    return (
                      <button
                        type="button"
                        key={
                          member?.contact_id ||
                          member?.user_id ||
                          index
                        }
                        className="bs-side-person"
                        onClick={
                          handleMemberPhotoClick
                        }
                      >
                        <img
                          src={
                            memberImage
                          }
                          alt={
                            memberName
                          }
                          onError={(
                            event
                          ) => {
                            event.currentTarget.src =
                              "/assets/images/user.png";
                          }}
                        />

                        <div className="bs-side-person-info">
                          <div className="bs-side-person-name-row">
                            <span className="bs-side-person-name">
                              {
                                memberName
                              }
                            </span>

                            {Number(
                              member
                                .verified_status
                            ) ===
                              1 && (
                              <i className="feather-check-circle bs-side-verified" />
                            )}
                          </div>

                          <span className="bs-side-person-role">
                            {
                              memberRole
                            }
                          </span>

                          <span className="bs-side-person-club">
                            {member?.client_name ||
                              "Bharat Sangam"}
                          </span>
                        </div>

                        <i className="feather-chevron-right bs-side-row-arrow" />
                      </button>
                    );
                  }
                )}
            </div>
          ) : (
            <EmptyState
              icon="feather-users"
              title="No members yet"
              text="Joined club members will appear here."
              link="/users"
              action="View users"
            />
          )}
        </div>

        {/* ==========================
            CLUBS
        ========================== */}
        <div className="bs-side-card">
          <SectionHeader
            title="Clubs"
            icon="feather-home"
            link="/clubs"
          />

          {clubLoading ? (
            <div className="bs-side-loading-list">
              {Array.from({
                length: 3,
              }).map(
                (_, index) => (
                  <div
                    key={index}
                    className="bs-side-skeleton-row"
                  >
                    <div className="bs-side-skeleton-avatar" />

                    <div className="bs-side-skeleton-lines">
                      <div className="bs-side-skeleton-line bs-side-skeleton-line--wide" />
                      <div className="bs-side-skeleton-line bs-side-skeleton-line--small" />
                    </div>
                  </div>
                )
              )}
            </div>
          ) : clubs.length >
            0 ? (
            <div className="bs-side-list">
              {clubs
                .slice(0, 5)
                .map(
                  (
                    club: any,
                    index: number
                  ) => {
                    const logo =
                      getClubLogo(
                        club
                      );

                    const clubName =
                      club?.client_name ||
                      "Club";

                    return (
                      <Link
                        key={
                          club?.client_id ||
                          index
                        }
                        to={getClubUrl(
                          club
                        )}
                        className="bs-side-club"
                      >
                        <img
                          src={
                            logo
                          }
                          alt={
                            clubName
                          }
                          onError={(
                            event
                          ) => {
                            event.currentTarget.src =
                              "/assets/images/user.png";
                          }}
                        />

                        <div className="bs-side-club-info">
                          <div className="bs-side-club-name-row">
                            <span className="bs-side-club-name">
                              {
                                clubName
                              }
                            </span>

                            {Number(
                              club?.verified_status
                            ) ===
                              1 && (
                              <i className="feather-check-circle bs-side-verified" />
                            )}
                          </div>

                          <span className="bs-side-club-followers">
                            {club?.total_followers ||
                              0}{" "}
                            Followers
                          </span>
                        </div>

                        <i className="feather-chevron-right bs-side-row-arrow" />
                      </Link>
                    );
                  }
                )}
            </div>
          ) : (
            <EmptyState
              icon="feather-home"
              title="No clubs found"
              text="Explore Bharat Sangam clubs and communities."
              link="/clubs"
              action="Explore clubs"
            />
          )}
        </div>
      </div>

      <style>{`
        /* ==========================================
           RIGHT SIDEBAR BASE
        ========================================== */

        .bs-rightchat {
          background:
            transparent !important;

          box-shadow:
            none !important;

          border:
            0 !important;
        }

        .bs-rightchat-content {
          overflow:
            visible !important;

          padding:
            0 !important;

          border:
            0 !important;

          border-radius:
            0 !important;

          background:
            transparent !important;

          box-shadow:
            none !important;
        }

        /* ==========================================
           CARD
        ========================================== */

        .bs-side-card {
          margin-bottom:
            12px;

          padding:
            16px;

          border:
            1px solid
            #E5EBF5;

          border-radius:
            18px;

          background:
            #FFFFFF;

          box-shadow:
            0 8px 28px
            rgba(
              10,
              35,
              84,
              0.045
            );

          overflow:
            hidden;
        }

        /* ==========================================
           SECTION HEADER
        ========================================== */

        .bs-side-section-header {
          display:
            flex;

          align-items:
            center;

          justify-content:
            space-between;

          gap:
            12px;

          margin-bottom:
            12px;
        }

        .bs-side-section-title {
          display:
            flex;

          align-items:
            center;

          gap:
            8px;

          min-width:
            0;
        }

        .bs-side-section-icon {
          display:
            flex;

          align-items:
            center;

          justify-content:
            center;

          width:
            29px;

          height:
            29px;

          flex-shrink:
            0;

          border-radius:
            9px;

          background:
            #EEF4FF;

          color:
            #2F6FE4;

          font-size:
            13px;
        }

        .bs-side-section-title h4 {
          margin:
            0;

          color:
            #334155;

          font-size:
            12.5px;

          font-weight:
            650;

          letter-spacing:
            0.1px;
        }

        .bs-side-view {
          display:
            inline-flex;

          align-items:
            center;

          gap:
            4px;

          flex-shrink:
            0;

          color:
            #2F6FE4;

          font-size:
            11px;

          font-weight:
            650;

          text-decoration:
            none;

          transition:
            0.15s ease;
        }

        .bs-side-view i {
          font-size:
            11px;

          transition:
            transform
            0.15s ease;
        }

        .bs-side-view:hover {
          color:
            #1447B0;
        }

        .bs-side-view:hover i {
          transform:
            translateX(2px);
        }

        .bs-side-badge {
          display:
            inline-flex;

          align-items:
            center;

          justify-content:
            center;

          min-width:
            24px;

          height:
            24px;

          padding:
            0 7px;

          border-radius:
            999px;

          background:
            #EEF4FF;

          color:
            #2F6FE4;

          font-size:
            10.5px;

          font-weight:
            800;
        }

        /* ==========================================
           FEATURE CARD
        ========================================== */

        .bs-side-feature {
          display:
            flex;

          align-items:
            center;

          gap:
            11px;

          min-width:
            0;

          padding:
            8px;

          margin:
            0 -8px -2px;

          border-radius:
            13px;

          text-decoration:
            none;

          transition:
            0.15s ease;
        }

        .bs-side-feature:hover {
          background:
            #F7F9FD;
        }

        .bs-side-feature-icon {
          display:
            flex;

          align-items:
            center;

          justify-content:
            center;

          width:
            42px;

          height:
            42px;

          flex-shrink:
            0;

          border-radius:
            13px;

          font-size:
            17px;
        }

        .bs-side-feature-icon--blue {
          background:
            #EEF4FF;

          color:
            #2F6FE4;
        }

        .bs-side-feature-icon--red {
          background:
            #FFF1F2;

          color:
            #E63946;
        }

        .bs-side-feature-content {
          flex:
            1;

          min-width:
            0;
        }

        .bs-side-feature-content h5 {
          overflow:
            hidden;

          margin:
            0;

          color:
            #334155;

          font-size:
            12.5px;

          font-weight:
            650;

          white-space:
            nowrap;

          text-overflow:
            ellipsis;
        }

        .bs-side-feature-content span {
          display:
            block;

          overflow:
            hidden;

          margin-top:
            3px;

          color:
            #94A3B8;

          font-size:
            10.8px;

          font-weight:
            550;

          white-space:
            nowrap;

          text-overflow:
            ellipsis;
        }

        .bs-side-feature-arrow {
          flex-shrink:
            0;

          color:
            #CBD5E1;

          font-size:
            14px;

          transition:
            0.15s ease;
        }

        .bs-side-feature:hover
        .bs-side-feature-arrow {
          color:
            #2F6FE4;

          transform:
            translateX(2px);
        }

        /* ==========================================
           MEMBER / CLUB LIST
        ========================================== */

        .bs-side-list {
          display:
            flex;

          flex-direction:
            column;

          gap:
            2px;
        }

        .bs-side-person,
        .bs-side-club {
          display:
            flex;

          align-items:
            center;

          gap:
            10px;

          width:
            100%;

          min-width:
            0;

          padding:
            8px;

          margin:
            0 -8px;

          border:
            0;

          border-radius:
            13px;

          background:
            transparent;

          color:
            inherit;

          text-align:
            left;

          text-decoration:
            none;

          cursor:
            pointer;

          transition:
            0.15s ease;
        }

        .bs-side-person:hover,
        .bs-side-club:hover {
          background:
            #F6F9FE;
        }

        .bs-side-person > img,
        .bs-side-club > img {
          display:
            block;

          width:
            38px;

          height:
            38px;

          flex-shrink:
            0;

          border:
            2px solid
            #FFFFFF;

          border-radius:
            50%;

          background:
            #EEF4FF;

          object-fit:
            cover;

          box-shadow:
            0 4px 12px
            rgba(
              15,
              23,
              42,
              0.08
            );
        }

        .bs-side-person-info,
        .bs-side-club-info {
          flex:
            1;

          min-width:
            0;
        }

        .bs-side-person-name-row,
        .bs-side-club-name-row {
          display:
            flex;

          align-items:
            center;

          gap:
            4px;

          min-width:
            0;
        }

        .bs-side-person-name,
        .bs-side-club-name {
          overflow:
            hidden;

          color:
            #475569;

          font-size:
            12.3px;

          font-weight:
            700;

          white-space:
            nowrap;

          text-overflow:
            ellipsis;
        }

        .bs-side-verified {
          flex-shrink:
            0;

          color:
            #2F6FE4;

          font-size:
            11px;
        }

        .bs-side-person-role,
        .bs-side-club-followers {
          display:
            block;

          overflow:
            hidden;

          margin-top:
            2px;

          color:
            #94A3B8;

          font-size:
            10.5px;

          font-weight:
            550;

          white-space:
            nowrap;

          text-overflow:
            ellipsis;
        }

        .bs-side-person-club {
          display:
            block;

          overflow:
            hidden;

          margin-top:
            2px;

          color:
            #2F6FE4;

          font-size:
            10.3px;

          font-weight:
            700;

          white-space:
            nowrap;

          text-overflow:
            ellipsis;
        }

        .bs-side-row-arrow {
          flex-shrink:
            0;

          color:
            #CBD5E1;

          font-size:
            14px;

          transition:
            0.15s ease;
        }

        .bs-side-person:hover
        .bs-side-row-arrow,
        .bs-side-club:hover
        .bs-side-row-arrow {
          color:
            #2F6FE4;

          transform:
            translateX(2px);
        }

        /* ==========================================
           EMPTY STATE
        ========================================== */

        .bs-side-empty {
          display:
            flex;

          flex-direction:
            column;

          align-items:
            center;

          padding:
            12px 8px 6px;

          text-align:
            center;
        }

        .bs-side-empty-icon {
          display:
            flex;

          align-items:
            center;

          justify-content:
            center;

          width:
            42px;

          height:
            42px;

          margin-bottom:
            9px;

          border-radius:
            13px;

          background:
            #F2F6FC;

          color:
            #94A3B8;

          font-size:
            17px;
        }

        .bs-side-empty h5 {
          margin:
            0;

          color:
            #475569;

          font-size:
            12px;

          font-weight:
            700;
        }

        .bs-side-empty p {
          max-width:
            205px;

          margin:
            4px 0 9px;

          color:
            #94A3B8;

          font-size:
            10.5px;

          line-height:
            1.5;
        }

        .bs-side-empty-action {
          display:
            inline-flex;

          align-items:
            center;

          justify-content:
            center;

          min-height:
            30px;

          padding:
            0 12px;

          border-radius:
            999px;

          background:
            #EEF4FF;

          color:
            #2F6FE4;

          font-size:
            10.5px;

          font-weight:
            700;

          text-decoration:
            none;

          transition:
            0.15s ease;
        }

        .bs-side-empty-action:hover {
          background:
            #2F6FE4;

          color:
            #FFFFFF;
        }

        /* ==========================================
           LOADING SKELETON
        ========================================== */

        .bs-side-loading-list {
          display:
            flex;

          flex-direction:
            column;
        }

        .bs-side-skeleton-row {
          display:
            flex;

          align-items:
            center;

          gap:
            10px;

          padding:
            8px 0;
        }

        .bs-side-skeleton-avatar {
          width:
            38px;

          height:
            38px;

          flex-shrink:
            0;

          border-radius:
            50%;

          background:
            linear-gradient(
              90deg,
              #EDF2F9 25%,
              #F8FAFD 37%,
              #EDF2F9 63%
            );

          background-size:
            400% 100%;

          animation:
            bs-side-shimmer
            1.3s ease
            infinite;
        }

        .bs-side-skeleton-lines {
          flex:
            1;
        }

        .bs-side-skeleton-line {
          height:
            7px;

          border-radius:
            999px;

          background:
            linear-gradient(
              90deg,
              #EDF2F9 25%,
              #F8FAFD 37%,
              #EDF2F9 63%
            );

          background-size:
            400% 100%;

          animation:
            bs-side-shimmer
            1.3s ease
            infinite;
        }

        .bs-side-skeleton-line--wide {
          width:
            68%;
        }

        .bs-side-skeleton-line--small {
          width:
            42%;

          margin-top:
            7px;
        }

        @keyframes bs-side-shimmer {
          0% {
            background-position:
              100% 0;
          }

          100% {
            background-position:
              -100% 0;
          }
        }

        /* ==========================================
           RESPONSIVE
        ========================================== */

        @media (
          max-width:
            1199px
        ) {
          .bs-side-card {
            padding:
              14px;
          }
        }
      `}</style>
    </div>
  );
};

export default Rightchat;
